import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useCreditNote } from '../contexts/CreditNoteContext';
import { useAppData } from '../../../providers/AppDataProvider';

interface LineRow {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface CreateCreditNoteModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

export function CreateCreditNoteModal({ show, onHide, onSuccess }: CreateCreditNoteModalProps) {
  const { createCreditNote, createLoading } = useCreditNote();
  const { customers } = useAppData();

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState<LineRow[]>([
    { id: generateId(), description: '', quantity: '1', unitAmount: '' },
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setSelectedCustomerId('');
      setDate(new Date().toISOString().split('T')[0]);
      setLineItems([{ id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
      setError(null);
    }
  }, [show]);

  const addLine = () => {
    setLineItems((prev) => [...prev, { id: generateId(), description: '', quantity: '1', unitAmount: '' }]);
  };

  const removeLine = (id: string) => {
    if (lineItems.length <= 1) return;
    setLineItems((prev) => prev.filter((r) => r.id !== id));
  };

  const updateLine = (id: string, field: keyof LineRow, value: string) => {
    setLineItems((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const customer = customers.find(c => c.id === selectedCustomerId);
    if (!customer) {
      setError('Select a customer');
      return;
    }
    const items = lineItems
      .map((r) => ({
        description: r.description.trim(),
        quantity: parseFloat(r.quantity) || 1,
        unitAmount: parseFloat(r.unitAmount) || 0,
      }))
      .filter((r) => r.description || r.unitAmount > 0);
    if (items.length === 0) {
      setError('Add at least one line item');
      return;
    }
    const total = items.reduce((s, li) => s + li.quantity * li.unitAmount, 0);
    try {
      await createCreditNote({
        contactId: selectedCustomerId,
        contactName: customer.name || customer.company || '',
        total,
        date,
        lineItems: items,
      });
      onSuccess?.();
      onHide();
    } catch (err: any) {
      setError(err?.message || 'Failed to create credit note');
    }
  };

  const customerOptions = customers
    .filter(c => c.name || c.company)
    .sort((a, b) => (a.name || a.company || '').localeCompare(b.name || b.company || ''));

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>New credit note</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              <i className="bi bi-exclamation-triangle me-2" />
              {error}
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Customer</Form.Label>
            <Form.Select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              required
            >
              <option value="">Select customer</option>
              {customerOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.company}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Form.Group>
          <Form.Label>Line items</Form.Label>
          {lineItems.map((row) => (
            <div key={row.id} className="d-flex gap-2 mb-2 align-items-start">
              <Form.Control placeholder="Description" value={row.description} onChange={(e) => updateLine(row.id, 'description', e.target.value)} className="flex-grow-1" />
              <Form.Control type="number" min="0" step="0.01" placeholder="Qty" style={{ width: '80px' }} value={row.quantity} onChange={(e) => updateLine(row.id, 'quantity', e.target.value)} />
              <Form.Control type="number" min="0" step="0.01" placeholder="Unit $" style={{ width: '100px' }} value={row.unitAmount} onChange={(e) => updateLine(row.id, 'unitAmount', e.target.value)} />
              <button type="button" className="btn btn-sm btn-icon btn-light-danger" onClick={() => removeLine(row.id)} disabled={lineItems.length <= 1}>
                <i className="bi bi-trash" />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-light-primary" onClick={addLine}>
            <i className="bi bi-plus me-1" /> Add line
          </button>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={onHide}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={createLoading}>
            {createLoading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Create credit note
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CreateCreditNoteModal;
